import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Sparkles } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import TiltCard from './TiltCard';
import SectionHeading from './SectionHeading';

const workExperiences = [
  {
    company: 'n-Formatix',
    role: 'Software Development Intern',
    period: 'June 2025 - August 2025',
    location: 'Remote',
    description: 'Built a full-stack medical slide viewer using React processing 100+ high-resolution images with real time rendering.',
    achievements: [
      'Pioneered AI-powered eye-tracking heatmap system reducing analysis time from 30 to 5 minutes significantly',
      'Engineered comprehensive analytics dashboard using PostgreSQL tracking 50+ medical researchers with insights',
      'Deployed containerized microservices with Docker supporting 100+ simultaneous users with instant responses'
    ],
    skills: ['React', 'PostgreSQL', 'Docker', 'AI/ML', 'Microservices']
  },
  {
    company: 'Unmute',
    role: 'AI/ML and BackEnd Developer',
    period: 'Feb 2025 - May 2025',
    location: 'Remote',
    description: 'Developed and optimized efficient robust backend systems using Firebase and MongoDB, improving response by 15%.',
    achievements: [
      'Engineered and streamlined scalable RESTful APIs for seamless data communication, increasing efficiency by 30%',
      'Optimized database architecture, fortified advanced security protocols, effectively mitigating data redundancy by 20%',
      'Collaborated with the team to develop AI/ML features, ensuring seamless integration and effective communication'
    ],
    skills: ['Firebase', 'MongoDB', 'RESTful APIs', 'AI/ML', 'Security']
  },
  {
    company: 'BNutty',
    role: 'Software Development Intern',
    period: 'May 2024 - Sep 2024',
    location: 'Valparaiso, Indiana',
    description: 'Automated 14 quality/quantity check forms via a React web app for reducing paperwork and 30% data entry errors.',
    achievements: [
      'Successfully reduced turnaround time from 24 hours to just 30 minutes for form submission to supervisor review',
      'Developed a scalable Django backend that improved efficiency 3-fold by automatic form compilation into PDF to CSV',
      'Presented real-time counts of 150K+ jars on React site via Django with OpenCV, achieving 98% accuracy on AWS'
    ],
    skills: ['React', 'Django', 'OpenCV', 'AWS', 'PDF/CSV']
  }
];

const Work: React.FC = () => {
  const [ref, isInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <section id="work" className="relative py-24 overflow-hidden">
      <div className="section-shell" ref={ref}>
        <SectionHeading
          eyebrow="Experience"
          title={<>A builder&apos;s timeline with <span className="gradient-text-vibrant">real product impact.</span></>}
          description="From healthcare tooling to data platforms, I focus on building systems that are measurable, reliable, and useful to real users."
        />

        <div className="relative space-y-8 before:absolute before:left-[1.35rem] before:top-4 before:h-[calc(100%-2rem)] before:w-px before:bg-gradient-to-b before:from-cosmic-aurora-emerald/60 before:to-transparent md:before:left-8">
          {workExperiences.map((work, index) => (
            <motion.div
              key={work.company}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="group relative"
            >
              <div className="absolute left-[0.62rem] top-8 z-10 h-6 w-6 rounded-full border border-cosmic-aurora-emerald/50 bg-cosmic-void shadow-aurora-emerald md:left-[1.45rem]" />
              <TiltCard intensity={2}>
                <div className="surface-panel surface-glow spotlight-card ml-10 rounded-[28px] border border-white/10 transition-all duration-300 hover:border-cosmic-aurora-emerald/50 md:ml-20">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                    <div className="flex-shrink-0">
                      <img 
                        src={`/Images/${work.company}.png`} 
                        alt={`${work.company} Logo`}
                        className="h-16 w-16 rounded-2xl object-contain bg-slate-100 p-2 transition-transform duration-300 group-hover:scale-110 dark:bg-white/5"
                      />
                    </div>

                    <div className="flex-1 space-y-4">
                      <div className="space-y-2">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cosmic-aurora-emerald/25 bg-cosmic-aurora-emerald/10 px-3 py-1 text-xs uppercase tracking-[0.24em] text-cosmic-aurora-emerald">
                              <Sparkles className="h-3.5 w-3.5" />
                              Experience
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 transition-colors duration-300 group-hover:text-cosmic-aurora-emerald dark:text-white">
                              {work.role}
                            </h3>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                            <div className="badge-pill gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{work.period}</span>
                            </div>
                            <div className="badge-pill gap-2">
                              <MapPin className="h-4 w-4" />
                              <span>{work.location}</span>
                            </div>
                          </div>
                        </div>
                        <h4 className="text-xl font-semibold gradient-text">
                          {work.company}
                        </h4>
                      </div>

                      <p className="text-[1.02rem] leading-8 text-slate-700 dark:text-slate-300">
                        {work.description}
                      </p>

                      <div className="space-y-3">
                        <h5 className="font-semibold text-slate-900 dark:text-white">Key achievements</h5>
                        <ul className="grid gap-3">
                          {work.achievements.map((achievement, idx) => (
                            <li key={idx} className="glass rounded-2xl px-4 py-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2">
                        {work.skills.map((skill) => (
                          <span
                            key={skill}
                            className="badge-pill border-cosmic-aurora-emerald/25 text-cosmic-aurora-emerald"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Work;
